import { createContext, useContext, useCallback } from 'react';

// Shared diff state for the PIF detail view. `changedPaths` is the compact
// { path: kind } map from the backend diff endpoint; `enabled` toggles in-view
// highlighting. Path convention matches Crm::Project::Pif::DiffService:
//   gds.<key> · areas[<i>].<bds>.<key> · <arr>[<idOrIndex>].<key>
export const PifDiffContext = createContext({
  changedPaths: {},
  changeByPath: {},
  enabled: false
});

export const usePifDiff = () => useContext(PifDiffContext);

// Returns mark(path) → a className suffix to append to an existing className.
// The class is kind-aware so the three change types are visually distinct:
//   added   → ' pif-diff-added'   (green)
//   removed → ' pif-diff-removed' (rose)  — value existed in baseline, blank now
//   changed → ' pif-diff-changed' (amber)
// Cheap + safe to call at every field render site.
export const useDiffMark = () => {
  const { changedPaths, enabled } = usePifDiff();
  return useCallback(
    (path) => {
      if (!enabled || !changedPaths || !changedPaths[path]) return '';
      const kind = changedPaths[path];
      if (kind === 'added') return ' pif-diff-added';
      if (kind === 'removed') return ' pif-diff-removed';
      return ' pif-diff-changed';
    },
    [changedPaths, enabled]
  );
};

// Returns rowMark(...prefixes) → a className suffix for TABLE rows: highlights a
// whole row when any changed path matches one of the row prefixes (row added /
// removed, or any of its cells changed). Pass both the id-based and index-based
// prefix since the diff engine matches array rows by id when available, else index.
export const useRowMark = () => {
  const { changedPaths, enabled } = usePifDiff();
  return useCallback(
    (...prefixes) => {
      if (!enabled || !changedPaths) return '';
      const keys = Object.keys(changedPaths);
      const valid = prefixes.filter((pfx) => pfx != null);

      // A whole added/removed row is keyed by the row prefix itself → colour the
      // row by that kind (green/rose). Otherwise a cell inside changed → amber.
      const rowKind = valid.map((pfx) => changedPaths[pfx]).find(Boolean);
      if (rowKind === 'added') return ' pif-diff-row-added';
      if (rowKind === 'removed') return ' pif-diff-row-removed';
      if (rowKind) return ' pif-diff-row';

      const cellChanged = valid.some((pfx) => keys.some((k) => k.startsWith(`${pfx}.`)));
      return cellChanged ? ' pif-diff-row' : '';
    },
    [changedPaths, enabled]
  );
};

const stripHtml = (v) => (v == null ? '' : String(v).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim());

// Optional tooltip "old → new" for a changed path.
export const useDiffTitle = () => {
  const { changeByPath, enabled } = usePifDiff();
  return useCallback(
    (path) => {
      if (!enabled || !changeByPath || !changeByPath[path]) return undefined;
      const c = changeByPath[path];
      return `${stripHtml(c.old) || '—'} → ${stripHtml(c.new) || '—'}`;
    },
    [changeByPath, enabled]
  );
};
